import type { CSSProperties } from "react";

interface MonoProps {
  children: React.ReactNode;
  color?: string;
  size?: number;
  style?: CSSProperties;
  /** Decorative-only text (e.g. a "·" separator): excluded from the a11y
      tree and from axe's color-contrast audit. */
  "aria-hidden"?: boolean;
}

/**
 * The system's meta / numeral atom.
 *
 * Named `Mono` for its original monospace face; owner ruling B (2026-08-14)
 * retired that face from every meta, label and numeral surface. The DS is
 * "Archivo, one family" and monospace is now reserved for code and file
 * paths (`LockChime.wav`, `Boombox/`, <code>, <pre>) — see --tls-font-mono
 * in app/globals.css. Column alignment does not depend on the face: Archivo
 * ships a real `tnum` feature (all ten digits at 579 units against
 * proportional 575–577), which `font-variant-numeric: tabular-nums` below
 * turns on. The name is kept because ~22 call sites import it and a rename
 * is churn, not a design decision.
 *
 * Weight 600 is the DS meta step (--ls-type-meta: 12.5px / 600 / tabular).
 * The monospace face carried its own apparent weight; dropping it without
 * naming one would have landed this text a step light against every label
 * beside it. Callers can still override via `style`, which spreads last.
 */
export function Mono({
  children,
  color = "var(--tls-fg-muted)",
  size = 12,
  style,
  "aria-hidden": ariaHidden,
}: MonoProps) {
  return (
    <span
      aria-hidden={ariaHidden}
      style={{
        fontFamily: "var(--tls-font-body)",
        fontWeight: 600,
        // §15.2: "Body, prose, labels" (this covers durations, counts,
        // meta rows) is a REM category — the user's font-size setting is
        // the most-used accessibility control on the web. `size` used to
        // be handed straight to `fontSize` as a bare number, which React
        // renders as `Npx` — invisible to OS/browser text-size scaling.
        // Converting at the 16px reference root keeps every existing
        // call site's rendered size byte-identical at 100%/default text
        // size (size={11} was 11px, is now 0.6875rem = 11px at root 16)
        // while making it actually grow under the OS 200% case §15.7
        // names as the harder failure mode than browser zoom.
        fontSize: `${size / 16}rem`,
        color,
        fontVariantNumeric: "tabular-nums",
        ...style,
      }}
    >
      {children}
    </span>
  );
}
